import { zodResolver } from '@hookform/resolvers/zod';
import type { ShowPasswordsState } from './types';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MIN_LENGTH, lowerCaseRegex, numberOrSymbolRegex, upperCaseRegex } from '@/domains/auth/constants/validation';
import AuthEvents from '@/domains/auth/events';
import { AuthPageContainer } from '@/domains/auth/layouts/AuthPageContainer/AuthPageContainer';
import { updatePassword } from '@/domains/auth/store/authSlice';
import { selectAuthUpdatePasswordIsLoading } from '@/domains/auth/store/selectors';
import { FormInput } from '@/components/form/controlled/FormInput/FormInput';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';
import { useEventEffect } from '@/hooks/useEventEffect';
import { DEFAULT_PATH, RootPaths } from '@/routes/paths';
import TokenService from '@/services/TokenService/TokenService';
import { useDispatch, useSelector } from '@/store/hooks';
import {
  AdaptiveButton,
  Form,
  List,
  LockIcon,
  Options,
  RuleFailIcon,
  RuleSuccessIcon,
  Text,
  Title,
  ValidationRules,
} from './UpdatePassword.styles';
import { validationSchema } from './validationSchema';
import ClosedEyeIcon from '../../assets/icons/closedEye.svg?react';
import OpenedEyeIcon from '../../assets/icons/openedEye.svg?react';

const defaultValues = {
  temporaryPassword: '',
  newPassword: '',
  confirmPassword: '',
};

type FormValues = typeof defaultValues;

export const UpdatePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const isLoading = useSelector(selectAuthUpdatePasswordIsLoading);

  const currentUserName = TokenService.getUserName();

  const [showPasswords, setShowPasswords] = useState<ShowPasswordsState>({
    temporaryPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const { temporaryPassword, newPassword, confirmPassword } = watch();

  const handleShowPasswordClick = (name: keyof ShowPasswordsState) => {
    setShowPasswords((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    dispatch(
      updatePassword({
        oldPassword: data.temporaryPassword,
        newPassword: data.newPassword,
        userName: currentUserName as string,
      }),
    );
    localStorage.removeItem(LocalStorageVariables.IsRequiredPasswordChange);
  };

  const endAdornment = (name: keyof ShowPasswordsState) => (
    <InputAdornment position="end">
      <IconButton
        data-testid="UpdatePasswordTogglePasswordVisibility"
        aria-label="toggle password visibility"
        edge="end"
        onClick={() => handleShowPasswordClick(name)}>
        {showPasswords[name] ? <ClosedEyeIcon /> : <OpenedEyeIcon />}
      </IconButton>
    </InputAdornment>
  );

  const validationRules = useMemo(() => {
    const newPasswordValue = newPassword.trim();

    return [
      { text: 'be at least 8 characters long (required).', applied: newPasswordValue.length >= MIN_LENGTH },
      {
        text: 'Include both lower and upper case characters.',
        applied: lowerCaseRegex.test(newPasswordValue) && upperCaseRegex.test(newPasswordValue),
      },
      {
        text: 'Include at least one number or symbol.',
        applied: numberOrSymbolRegex.test(newPasswordValue),
      },
    ];
  }, [newPassword]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ' ') event.preventDefault();
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text').replace(/\s+/g, '');
    document.execCommand('insertText', false, paste);
  };

  useEffect(() => {
    clearErrors('temporaryPassword');
  }, [temporaryPassword, clearErrors]);

  useEffect(() => {
    clearErrors('newPassword');
  }, [newPassword, clearErrors]);

  useEffect(() => {
    clearErrors('confirmPassword');
  }, [confirmPassword, clearErrors]);

  useEventEffect(AuthEvents.RedirectToDefault, () => navigate(DEFAULT_PATH, { replace: true }));
  useEventEffect(AuthEvents.RedirectToOtp, (username) => navigate(RootPaths.OTP, { state: { username } }));

  return (
    <AuthPageContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Title>
          <Typography data-testid="UpdatePasswordTitle" variant={isSmallScreen ? 'h5' : 'h2'}>
            Update your password
          </Typography>
          <Typography variant={isSmallScreen ? 'body2' : 'body1'}>
            You are log in with temporary password. Please update your password.
          </Typography>
        </Title>
        <FormInput
          name="temporaryPassword"
          control={control}
          label="Temporary Password"
          InputProps={{ endAdornment: endAdornment('temporaryPassword') }}
          type={showPasswords['temporaryPassword'] ? 'text' : 'password'}
          errorMessage={errors.temporaryPassword?.message}
        />
        <FormInput
          name="newPassword"
          control={control}
          label="New Password"
          InputProps={{ endAdornment: endAdornment('newPassword') }}
          type={showPasswords['newPassword'] ? 'text' : 'password'}
          errorMessage={errors.newPassword?.message}
          inputProps={{ onKeyDown: handleKeyDown, onPaste: handlePaste }}
        />
        <FormInput
          name="confirmPassword"
          control={control}
          label="Confirm Password"
          InputProps={{ endAdornment: endAdornment('confirmPassword') }}
          type={showPasswords['confirmPassword'] ? 'text' : 'password'}
          errorMessage={errors.confirmPassword?.message}
          inputProps={{ onKeyDown: handleKeyDown, onPaste: handlePaste }}
        />
        <ValidationRules>
          <LockIcon />
          <List>
            <Typography variant="body1Bold">Your password should:</Typography>
            {validationRules.map((rule) => (
              <Text applied={!!rule.applied} key={rule.text}>
                {rule.applied ? <RuleSuccessIcon /> : <RuleFailIcon />}
                {rule.text}
              </Text>
            ))}
          </List>
        </ValidationRules>
        <Options>
          <AdaptiveButton
            data-testid="UpdatePasswordLoginButton"
            width="fluid"
            size="large"
            type="submit"
            disabled={isLoading}>
            LOG IN
          </AdaptiveButton>
        </Options>
      </Form>
    </AuthPageContainer>
  );
};
